import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import MaterialTable, {MTableBody, MTableToolbar, MTableHeader} from 'material-table';
import { TableCell, TableRow, TableFooter } from '@material-ui/core';
import MTableFilterRow from '../material-table-overrides/MTableFilterRow';
import YearSelect from "../common/YearSelect";
import DownloadLink from "../common/DownloadLink";
import ConfirmModal from '../common/ConfirmModal';
import { years, tableIcons } from "../../helpers";
import { arrayToLookup, localization, modifyDefaultOptions, getStickyColumns } from '../../helpers/table';
import { renderNamesString, renderNullFloatValues } from '../../pages/index/Shared';
import useAPI from "../../hooks/useAPI";
import useToast from '../../hooks/useToast';
import useUserContext from '../../hooks/useUserContext';
import LoadingTable from '../table/LoadingTable';
import FilterMultiSelect from '../table/FilterMultiSelect';
import { dateRenderer } from '../../services/formatSvc';
import { BaseRequests } from '../../requests/BaseRequests';
import { ReceiptsActions } from '../common/Actions/ReceiptsActions';
import { isAdmin, isModerator, isStratAdmin, isStratCurator, isStrategyTeam } from '../../services/rolesSvc';

const leftValues = [0, 92, 182, 358];

const NioktrOwnFundsTab = ({ stratId, strategyName }) => {
  const api = useAPI()
  const addToast = useToast();
  const history = useHistory();
  const userCtx = useUserContext();
  const [data, setData]= useState([])
  const [year, setYear] = useState(new Date().getFullYear());
  const [isLoaded, setIsLoaded] = useState(true);
  const [isModalDelete, setIsModalDelete] = useState({ show: false, id: undefined });

  const properties = ['sum', 'incomesSum', 'internalFundSum'];

   const roleModel = useMemo(() => ({
    canEdit: isAdmin(userCtx) || isModerator(userCtx) || isStratAdmin(userCtx, 4) || isStratCurator(userCtx, 4),
    canDelete: isAdmin(userCtx)
  }), [userCtx])

  const actions = {
    edit: (e) => history.push(`/policies/${stratId}/budget/nioktr-own-funds/${e.id}/edit-receipt`),
    delete: (e) => setIsModalDelete({ show: true, id: e.id }),
  };

  const handleDelete = async () => {
    const result = await api.del(`/factdata/internalscifunds/${isModalDelete.id}`);

    result.hasOwnProperty('error')
    ? addToast.error(`${result.error.message}, удаление невозможно`)
    : (
      addToast.success('Запись о собственных средствах удалена'),
      setData(data.filter((e) => (e.id !== result.id))),
      setIsModalDelete({ show: false, id: undefined })
    )
  }

  const sumValues = useCallback((result) => {
    let sums = {};

    properties.forEach((prop) => {
      let sumsOneArr = [];

      result?.map((e) => {
        sumsOneArr.push(e[prop])
      })

      if (sumsOneArr.length > 0) {
        sums[prop] = Number(sumsOneArr.reduce((a, b) => { return a + b }))
      }
    })
    return sums;
  }, [])

  const components = useMemo(() => {
    return {
      Toolbar: (props) => (
        <div style={{ marginTop: '1.5em', marginBottom: '1em' }}>
          <div style={styledToolbarContainer}>
            <div style={styledYearContainer}>
              <div style={styledYearTitle}>Год</div>
              <div style={{ width: '100%' }}>
                <YearSelect
                  year={year}
                  years={[...years.filter((e) => e != 2021)]}
                  onChange={(e) => setYear(e.value)}
                />
              </div>
            </div>

            <div style={styledRightToolbar}>
              <div>
                <MTableToolbar {...props} />
              </div>

              <div style={{ marginLeft: 16 }}>
                <DownloadLink
                  variant="outline-primary"
                  url={BaseRequests.getUrlSearch(`/factdata/internalscifunds/0/export`, { year })}
                  filename={`Собственные средства на НИОКТР_${dateRenderer(new Date())}.xlsx`}
                >
                  Выгрузить в Excel
                </DownloadLink>
              </div>

              {roleModel.canEdit && <div style={{ flex: 'none', marginLeft: 16 }}>
                <NavLink to={`/policies/${stratId}/budget/nioktr-own-funds/0/edit-receipt`}>
                  <Button variant="primary">+ Добавить</Button>
                </NavLink>
              </div>}

            </div>
          </div>
        </div>
      ),
      FilterRow: (props) => <MTableFilterRow {...props} />,
      Body: (props) => {
        return <>
            <MTableBody {...props} isTreeData={false} />
            {props.renderData.length > 0 && (
              <TableFooter>
                <TableRow>
                  {props.columns.map((column, index) => (
                    <TableCell
                      style={index > 3
                        ? {
                            color: '#212529',
                            fontWeight: 700,
                            fontSize: 14,
                          }
                        : {
                            color: '#212529',
                            fontWeight: 700,
                            fontSize: 14,
                            position: 'sticky',
                            background: 'white',
                            boxShadow: 'rgba(0, 0, 0, 0.15) 5px 0px 5px',
                            zIndex: 15,
                            left: leftValues[index],
                          }
                      }
                      key={index}
                    >
                      {index === 3 ? 'Итого' : properties.includes(column.field) ? renderNullFloatValues(sumValues(props.renderData)[column.field]) : ""}
                    </TableCell>
                  ))}
                </TableRow>
              </TableFooter>
            )}
        </>
      },
    };
  }, [year, data]);

  const converterDate = useCallback((a, b) => {
    return b.substring(0, 10).replace(/-/g, '') - a.substring(0, 10).replace(/-/g, '')
  }, [])

  const getData = async (year) => {
    setIsLoaded(false);
    try {
      const yearQuery = year ? `?year=${year}` : '';
      const result = await api.get(`/factdata/internalscifunds${yearQuery}`);
      const reformedResult = result.data.map((x, i) => {
        return {
          ...{
            num: i + 1,
            docNameNum: x?.incomes?.sort((a, b) => converterDate(a.docDate, b.docDate)).map((el) => el.docName).join(',\n'),
            date: x?.incomes?.sort((a, b) => converterDate(a.docDate, b.docDate)).map((el) => `${el?.docNum} от ${dateRenderer(el.docDate)}`).join(',\n'),
            costItem: x?.incomes?.sort((a, b) => converterDate(a.docDate, b.docDate)).map((el) => el.costItem).join(',\n'),
            comment: x?.incomes?.sort((a, b) => converterDate(a.docDate, b.docDate)).map((el) => el.comment).join(',\n'),
            policy: strategyName || '',
          }, ...x
        }
      });
      setData(reformedResult);
    } catch (error) {
      console.error(`Ошибка при загрузке данных с сервера: ${error}`)
    } finally {
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    getData(year)
  }, [year]);

  return (
    <>
      <LoadingTable spinnerStyle={{ marginTop: '50px' }} loadingObject={{ isLoaded }}>
        <section>
          <div style={styledTable}>
            <MaterialTable
              options={modifyDefaultOptions({
                columnsButton: true,
                maxBodyHeight: 'calc(100vh - 400px)',
                rowStyle: () => ({
                  backgroundColor: '#FFF',
                  whiteSpace: 'pre-wrap',
                }),
                headerStyle: {
                  height: 72,
                  position: 'sticky',
                },
                filterCellStyle: {
                  position: 'sticky',
                  top: 108,
                  background: 'white',
                },
              })}
              icons={tableIcons}
              localization={localization}
              columns={
                [
                  ...getStickyColumns([
                    {
                      title: 'Действ.',
                      render: (rowData) => 
                        (<ReceiptsActions
                          props={rowData}
                          actions={actions}
                          userCtx={userCtx}
                          policyId={stratId}
                          roleModel={roleModel}
                          isInsideTabs
                        />),
                      width: '92px',
                    },
                    {
                      title: '№ п/п',
                      field: 'num',
                      width: '90px',
                      render: (data) => 
                        (<TableCell align='center' style={{ border: 'none' }}>
                          {data.num}
                        </TableCell>),
                    },
                    {
                      title: 'Вид доходов',
                      field: 'type',
                      lookup: arrayToLookup('type', data),
                      filterCellStyle: { width: '176px' },
                      filterComponent: FilterMultiSelect,
                      width: '176px',
                      maxWidth: '176px',
                    },
                    {
                      title: 'Реквизиты первичных документов, подтверждающие получение доходов',
                      field: 'docNum',
                      filterCellStyle: { minWidth: '190px', maxWidth: '190px'},
                      filtering: true,
                      width: '190px',
                      maxWidth: '190px',
                    }
                  ], 0),
                  {
                    title: 'Краткая характеристика доходов',
                    field: 'descr',
                    filterCellStyle: { minWidth: '176px', maxWidth: '176px'},
                    filtering: true,
                    width: '176px',
                  },
                  {
                    title: 'Сумма доходов, направленная на мероприятия Программы развития, руб.',
                    field: 'sum',
                    filterCellStyle: { minWidth: '280px', maxWidth: '280px'},
                    filtering: true,
                    width: '280px',
                    render: (data) => renderNullFloatValues(Number(data.sum)),
                  },
                  {
                    title: 'Наименование документа, подтверждающего выплату денежных средств',
                    field: 'docNameNum',
                    lookup: arrayToLookup('docNameNum', data),
                    filterCellStyle: { minWidth: '176px', maxWidth: '176px'},
                    filterComponent: FilterMultiSelect,
                    width: '176px',
                  },
                  {
                    title: '№, дата документа, подтверждающего выплату денежных средств',
                    field: 'date',
                    lookup: arrayToLookup('date', data),
                    filterCellStyle: { minWidth: '165px', maxWidth: '165px'},
                    filterComponent: FilterMultiSelect,
                    width: '165px',
                  },
                  {
                    title: 'Статья расходов',
                    field: 'costItem',
                    lookup: arrayToLookup('costItem', data),
                    filterCellStyle: { minWidth: '176px', maxWidth: '176px'},
                    filterComponent: FilterMultiSelect,
                    width: '176px',
                  },
                  {
                    title: 'Сумма выплат (всего), руб.',
                    field: 'incomesSum',
                    filterCellStyle: { minWidth: '185px', maxWidth: '185px'},
                    filtering: true,
                    width: '185px',
                    render: (data) => renderNullFloatValues(Number(data.incomesSum)),
                  },
                  {
                    title: 'Сумма за счет собственных средств, направленных на проведение прикладных научных исследований и (или) экспериментальных разработок, руб.',
                    field: 'internalFundSum',
                    filterCellStyle: { minWidth: '330px', maxWidth: '330px'},
                    filtering: true,
                    width: '330px',
                    render: (data) => renderNullFloatValues(Number(data.internalFundSum)),
                  },
                  {
                    title: 'Примечание',
                    field: 'comment',
                    filterCellStyle: { minWidth: '185px', maxWidth: '185px'},
                    filtering: true,
                    width: '185px',
                    render: (data) => {
                      if (data.comment.endsWith(',\n')) {
                        return data.comment.slice(0, data.comment.length - 2);
                      }
                      return data.comment;
                    },
                  },
                  {
                    title: 'Стратегический проект или раздел научно-исследовательской политики Программы развития университета, в рамках реализации которого (ых) поступили денежные средства',
                    field: 'policy',
                    filterCellStyle: { minWidth: '330px'},
                    filtering: false,
                    width: '330px',
                  },
                ]
              }
              data={data}
              components={components}
              title=""
            />
          </div>
        </section>
      </LoadingTable>
      <ConfirmModal
        show={isModalDelete.show}
        onClose={() => setIsModalDelete({ show: false, id: undefined })}
        onConfirm={handleDelete}
        headerText="Удаление лицевого счета"
        message={`Вы действительно хотите удалить связь лицевого счета с политикой развития ${strategyName}?`}
      />
    </>
  );
}

export default NioktrOwnFundsTab;

const styledToolbarContainer = { 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'space-between',
};

const styledYearContainer = {
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  position: 'relative',
  zIndex: 21,
  minWidth: '144px',
  alignItems: 'start',
};

const styledYearTitle = {
  color: 'black', 
  fontSize: '16px', 
  marginBottom: '5px', 
  fontWeight: '600',
};

const styledRightToolbar = { 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'space-between',
};

const styledExpandButton = {
  display: 'inline-flex',
  border: 'none',
  backgroundColor: 'transparent',
  color: '#0D6EFD',
  fontSize: 16,
  fontFamily: 'Open-sans, sans-serif',
  padding: 0,
  columnGap: 10,
  textAlign: 'right',
}

const styledTable = {
  maxWidth: '100%',
  marginTop: 20,
};
